import React, { useState, useEffect } from "react";
import { Box, Stack, Grid } from "@mui/material";
import PropTypes from "prop-types";
import SoftButton from "../../../components/SoftButton";
import SoftBox from "../../../components/SoftBox";
import SoftTypography from "../../../components/SoftTypography";

import SoftSelect from "../../../components/SoftSelect";
import { useMerchantStore } from "../../../stores/merchant";
import { useGetBrand, useGetBrands } from "../../ApiKeyManagement/hooks";
import ColorAndPreview from "./ColorAndPreview";
import LogoUpload from "./LogoUpload";
import {
  cashierThemePresets,
  DEFAULT_CASHIER_THEME_PRESET_ID,
} from "../../../constants/cashierThemePresets";

function SettingsColorPresets({ loading, setCashierTheme }) {
  const { setSelectedPreviewBrand, merchant } = useMerchantStore();
  const { data: brands } = useGetBrands();
  const [selectedBrand, setSelectedBrand] = useState(null);
  const [selectedPresetId, setSelectedPresetId] = useState(DEFAULT_CASHIER_THEME_PRESET_ID);
  const listBrand = brands?.find((brand) => brand._id === selectedBrand?.value);
  const { data: selectedBrandData } = useGetBrand(selectedBrand?.value);
  const brand = selectedBrandData || listBrand || null;
  const currentLogoUrl =
    brand?.logo ||
    brand?.v3CashierTheme?.overrides?.brand?.logoUrl ||
    brand?.cashierTheme?.brand?.logoUrl ||
    merchant?.logo ||
    null;

  const brandOptions = brands?.map((brand) => ({
    label: brand.name,
    value: brand._id,
  }));

  const onBrandChange = (e) => {
    setSelectedPreviewBrand(e.value);
    const brand = brands?.find((brand) => brand._id === e.value);
    setSelectedBrand({ label: brand?.name, value: brand?._id });
  };

  const resolvePresetId = (theme) => {
    if (!theme) {
      return DEFAULT_CASHIER_THEME_PRESET_ID;
    }
    if (typeof theme === "string") {
      return cashierThemePresets.some((preset) => preset.id === theme)
        ? theme
        : DEFAULT_CASHIER_THEME_PRESET_ID;
    }
    if (typeof theme?.presetId === "string") {
      return cashierThemePresets.some((preset) => preset.id === theme.presetId)
        ? theme.presetId
        : DEFAULT_CASHIER_THEME_PRESET_ID;
    }
    return DEFAULT_CASHIER_THEME_PRESET_ID;
  };

  const buildThemePayload = (brandTheme, presetId) => {
    if (brandTheme && typeof brandTheme === "object") {
      return { ...brandTheme, presetId };
    }
    return { presetId };
  };

  const applyPreset = (presetId, brandTheme) => {
    setSelectedPresetId(presetId);
    setCashierTheme(buildThemePayload(brandTheme, presetId));
  };

  useEffect(() => {
    if (!selectedBrand?.value && brands?.length > 0) {
      setSelectedBrand({ label: brands[0]?.name, value: brands[0]?._id });
    }
  }, [brands]);

  useEffect(() => {
    setSelectedPreviewBrand(brand?._id);
    const brandTheme = brand?.v3CashierTheme || brand?.cashierTheme;
    applyPreset(resolvePresetId(brandTheme), brandTheme);
  }, [brand]);

  return (
    <Box sx={{ textAlign: "left", paddingRight: 3 }}>
      <SoftBox sx={{ maxWidth: 260, mb: 2 }}>
        <SoftTypography variant="caption" fontWeight="bold" sx={{ display: "block", mb: 0.5 }}>
          Brand
        </SoftTypography>
        <SoftSelect
          options={brandOptions}
          placeholder={"Select Brand"}
          onChange={onBrandChange}
          value={selectedBrand}
        />
      </SoftBox>

      <Stack
        direction={{ xs: "column", md: "row" }}
        justifyContent="space-between"
        alignItems="center"
        width={1}
        spacing={3}
        mb={3}
      >
        <SoftTypography variant="h4">Logo</SoftTypography>
        <SoftBox display="flex" justifyContent="flex-start" gap={2} mt={4}>
          <SoftButton
            variant="gradient"
            color="info"
            type="submit"
            disabled={loading}
            isLoading={loading}
          >
            Save changes
          </SoftButton>
        </SoftBox>
      </Stack>
      <SoftBox p={1}>
        <LogoUpload currentLogoUrl={currentLogoUrl} />
      </SoftBox>
      <SoftBox mt={3} mb={1}>
        <SoftTypography variant="h4">Theme</SoftTypography>
      </SoftBox>
      <Grid container>
        <Grid item xs={12}>
          <ColorAndPreview
            selectedPresetId={selectedPresetId}
            brandLogoUrl={currentLogoUrl}
            brand={brand}
            onPresetChange={(presetId) =>
              applyPreset(presetId, brand?.v3CashierTheme || brand?.cashierTheme)
            }
          />
        </Grid>
      </Grid>
    </Box>
  );
}

export default SettingsColorPresets;

// Typechecking props of the SettingsColorPresets
SettingsColorPresets.propTypes = {
  setCashierTheme: PropTypes.func,
  loading: PropTypes.bool,
};
